import type React from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Icon, type IconName } from '@/common/ui/Icon'

export type MediaSelectCardProps = {
    title: string
    description: string
    imageUrl?: string | null
    iconName?: IconName
    disabled?: boolean
    className?: string
    titleClassName?: string
    descriptionClassName?: string
    imageClassName?: string
    placeholderVariant?: 'icon' | 'blank'
    placeholderClassName?: string
    ariaLabel?: string
    onClick?: () => void
    onRemove?: () => void
}

export const MediaSelectCard = ({
    title,
    description,
    imageUrl,
    iconName = 'gallery-import',
    disabled,
    className,
    titleClassName,
    descriptionClassName,
    imageClassName,
    placeholderVariant = 'icon',
    placeholderClassName,
    ariaLabel,
    onClick,
    onRemove,
}: MediaSelectCardProps) => {
    const handleRemoveClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        onRemove?.()
    }

    const handleClick = () => {
        if (disabled) return
        onClick?.()
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onClick?.()
        }
    }

    return (
        <div
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={cn(
                'flex gap-3 items-center p-3 rounded-[10px] transition-colors bg-[rgba(255,255,255,0.05)]',
                'hover:bg-[rgba(255,255,255,0.1)] text-left cursor-pointer',
                { 'cursor-not-allowed opacity-70': disabled },
                className
            )}
            role='button'
            tabIndex={0}
            aria-label={ariaLabel}
            aria-disabled={disabled}
        >
            {imageUrl ? (
                <div
                    className={cn(
                        'flex items-center justify-center rounded-lg w-[50px] h-[50px] bg-cover bg-center',
                        imageClassName
                    )}
                    style={{ backgroundImage: `url(${imageUrl})` }}
                />
            ) : (
                <>
                    {placeholderVariant === 'blank' ? (
                        <div className={cn('rounded-[8px] w-[50px] h-[50px] bg-white', placeholderClassName)} />
                    ) : (
                        <div className='bg-[rgba(255,255,255,0.05)] flex items-center justify-center rounded-lg w-[50px] h-[50px]'>
                            <Icon name={iconName} className='w-5 h-5 text-white' />
                        </div>
                    )}
                </>
            )}
            <div className='flex flex-col gap-2 items-start flex-1'>
                <p
                    className={cn(
                        'font-normal text-xs leading-[22px] text-white',
                        { 'opacity-50': disabled },
                        titleClassName
                    )}
                >
                    {title}
                </p>
                <p
                    className={cn(
                        'font-normal text-xs leading-normal text-[rgba(171,167,175,0.6)]',
                        { 'opacity-50': disabled },
                        descriptionClassName
                    )}
                >
                    {description}
                </p>
            </div>
            {onRemove && (
                <button
                    type='button'
                    onClick={handleRemoveClick}
                    className='flex items-center justify-center w-6 h-6 rounded-full bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.2)] transition-colors cursor-pointer'
                    aria-label='Удалить выбор'
                >
                    <Icon name='cross' className='w-3 h-3 text-white' />
                </button>
            )}
        </div>
    )
}
